import { useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import _ from "lodash";
import { useState } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import * as yup from "yup";

import {
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import RemoveCircle from "@material-ui/icons/RemoveCircle";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import Table, { TableControls } from "src/components/Table";
import FormikTextField from "src/components/form/FormikTextField";
import {
  listStaffEmails,
  listStaffEmails_listStaffEmails
} from "src/graphql/generated/listStaffEmails";
import {
  staffEmailCreate,
  staffEmailCreateVariables
} from "src/graphql/generated/staffEmailCreate";
import {
  staffEmailDelete,
  staffEmailDeleteVariables
} from "src/graphql/generated/staffEmailDelete";
import {
  LIST_STAFF_EMAILS,
  CREATE_STAFF_EMAIL,
  DELETE_STAFF_EMAIL
} from "src/graphql/staffEmail";
import useMutation from "src/util/useMutation";

const staffEmailSchema = yup.object().shape({
  fullName: yup.string().required("Required"),
  email: yup.string().required("Required").email()
});

function AddStaffEmailDialog({
  open,
  onClose,
  handleCreateStaffEmail
}: {
  open: boolean;
  onClose: () => void;
  handleCreateStaffEmail: (values: {
    fullName: string;
    email: string;
  }) => Promise<void>;
}): JSX.Element {
  const onSubmit = async (params: { fullName: string; email: string }) => {
    await handleCreateStaffEmail(params);
    onClose();
  };

  return (
    <>
      <Dialog open={open} maxWidth="md">
        <DialogTitle>Add Staff Email</DialogTitle>
        <Formik
          initialValues={{
            fullName: "",
            email: ""
          }}
          onSubmit={onSubmit}
          validationSchema={staffEmailSchema}
        >
          {({ isSubmitting }) => (
            <Form>
              <DialogContent>
                <FormikTextField
                  fullWidth
                  label="Name"
                  name="fullName"
                  type="text"
                />
                <FormikTextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                />
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    onClose();
                  }}
                  color="primary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  color="primary"
                  variant="contained"
                >
                  {isSubmitting ? "Creating..." : "Add Staff Member"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

type StaffEmailRow = { id: string } & listStaffEmails_listStaffEmails;

function StaffEmailTable({
  rows,
  handleAddStaffEmail,
  handleDeleteStaffEmail
}: {
  rows: StaffEmailRow[];
  handleAddStaffEmail: ({
    fullName,
    email
  }: {
    fullName: string;
    email: string;
  }) => Promise<void>;
  handleDeleteStaffEmail: (email: string) => Promise<void>;
}): JSX.Element {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [addStaffEmailDialogOpen, toggleAddStaffEmailDialog] = useState(false);

  return (
    <>
      <TableControls
        addButtons={[
          {
            label: "Add Staff Member",
            onClick: () => toggleAddStaffEmailDialog(true)
          }
        ]}
      />
      <Table
        columns={[
          {
            key: "email",
            title: "Email",
            renderValue: row => row.email
          },
          {
            key: "fullName",
            title: "Name",
            renderValue: row => row.fullName
          }
        ]}
        rows={rows}
        rowActions={[
          {
            icon: <RemoveCircle />,
            tooltipLabel: "Remove Staff Account",
            confirm: {
              title: "Remove Staff Account",
              description:
                "Are you sure you want to remove this staff account? All admins with this email will also be deleted."
            },
            onClick: row => handleDeleteStaffEmail(row.email)
          }
        ]}
        emptyState="No results"
        totalCount={rows.length}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newRowsPerPage => {
          setPageSize(newRowsPerPage);
          setPage(0);
        }}
        page={page}
        onChangePage={setPage}
      />
      <AddStaffEmailDialog
        open={addStaffEmailDialogOpen}
        onClose={() => {
          toggleAddStaffEmailDialog(false);
        }}
        handleCreateStaffEmail={handleAddStaffEmail}
      />
    </>
  );
}

export function StaffEmailAdmin(): JSX.Element {
  const { loading, error, data } = useQuery<listStaffEmails>(LIST_STAFF_EMAILS);

  const [addStaffEmail] = useMutation<
    staffEmailCreate,
    staffEmailCreateVariables
  >(CREATE_STAFF_EMAIL, {
    onErrorSnackbarMessage: "Error creating staff email",
    refetchQueries: ["listStaffEmails"],
    awaitRefetchQueries: true
  });

  const [deleteStaffEmail] = useMutation<
    staffEmailDelete,
    staffEmailDeleteVariables
  >(DELETE_STAFF_EMAIL, {
    onErrorSnackbarMessage: "Error deleting staff email",
    refetchQueries: ["listStaffEmails"],
    awaitRefetchQueries: true
  });

  const handleAddStaffEmail = async ({
    fullName,
    email
  }: {
    fullName: string;
    email: string;
  }) => {
    await addStaffEmail({ variables: { fullName, email } });
  };

  const handleDeleteStaffEmail = async (email: string) => {
    await deleteStaffEmail({ variables: { email } });
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <Container>
      <PageTitle title="Staff Emails" />
      <StaffEmailTable
        handleAddStaffEmail={handleAddStaffEmail}
        handleDeleteStaffEmail={handleDeleteStaffEmail}
        rows={data!.listStaffEmails.map(
          (row: listStaffEmails_listStaffEmails) => ({ id: row!.email, ...row })
        )}
      />
    </Container>
  );
}
